import { useEffect, useState } from "react"
import api from "../../api/axios"
import { ClipboardList, Filter, Download, Scan, Fingerprint, QrCode } from "lucide-react"
import { Card } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { SearchInput } from "../../components/ui/Input"
import { Select } from "../../components/ui/Select"
import { Badge } from "../../components/ui/Badge"
import { EmptyState } from "../../components/ui/EmptyState"
import { TableSkeleton } from "../../components/ui/Skeleton"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/Table"
import { fr } from "../../locales/fr"
import { accessStatusLabel, accessMethodLabel } from "../../locales/fr"

export default function AccessLogs() {
  const [logs, setLogs] = useState([])
  const [filteredLogs, setFilteredLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    filterLogs()
  }, [logs, search, statusFilter])

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get("/access/logs")
      setLogs(Array.isArray(res.data) ? res.data : [])
    } catch (e) {
      console.error("Failed to load access logs", e)
    }
    setLoading(false)
  }

  const filterLogs = () => {
    let result = [...logs]

    if (search) {
      result = result.filter((l) =>
        l.member?.toLowerCase().includes(search.toLowerCase())
      )
    }

    if (statusFilter !== "all") {
      result = result.filter((l) => l.status === statusFilter)
    }

    setFilteredLogs(result)
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "granted":
        return <Badge variant="success">{fr.status.granted}</Badge>
      case "denied":
        return <Badge variant="destructive">{fr.status.denied}</Badge>
      default:
        return <Badge variant="outline">{accessStatusLabel(status)}</Badge>
    }
  }

  const getMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case "face":
        return <Scan className="h-4 w-4" />
      case "fingerprint":
        return <Fingerprint className="h-4 w-4" />
      case "qr":
        return <QrCode className="h-4 w-4" />
      default:
        return <ClipboardList className="h-4 w-4" />
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{fr.accessLogs.title}</h1>
          <p className="text-muted-foreground">{fr.accessLogs.subtitle}</p>
        </div>
        <Button variant="outline">
          <Download className="mr-2 h-4 w-4" />
          {fr.common.export}
        </Button>
      </div>

      <Card className="p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchInput
              placeholder={fr.accessLogs.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-44"
            >
              <option value="all">{fr.status.all}</option>
              <option value="granted">{fr.status.granted}</option>
              <option value="denied">{fr.status.denied}</option>
            </Select>
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <TableSkeleton rows={10} columns={4} />
        ) : filteredLogs.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={fr.accessLogs.emptyTitle}
            description={
              search || statusFilter !== "all"
                ? fr.accessLogs.emptyFiltered
                : fr.accessLogs.emptyInitial
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{fr.accessLogs.table.member}</TableHead>
                <TableHead>{fr.accessLogs.table.method}</TableHead>
                <TableHead>{fr.accessLogs.table.status}</TableHead>
                <TableHead>{fr.accessLogs.table.dateTime}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <span className="font-medium text-foreground">{log.member}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      {getMethodIcon(log.method)}
                      <span>{accessMethodLabel(log.method)}</span>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(log.status)}</TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{log.createdAt}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {filteredLogs.length > 0 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-4">
            <p className="text-sm text-muted-foreground">
              {fr.pagination.logs(filteredLogs.length, logs.length)}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>
                {fr.common.previous}
              </Button>
              <Button variant="outline" size="sm" disabled>
                {fr.common.next}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
